import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { PaginationState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import CustomSelect from "Common/Components/InputFields/CustomSelect";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import callApi from "Common/utils/apiCaller";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import _ from "lodash";
import moment from "moment";

const GREEN = "bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400";
const SKY = "bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400";
const VIOLET = "bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400";
const YELLOW = "bg-yellow-100 text-yellow-600 dark:bg-yellow-500/20 dark:text-yellow-400";
const RED = "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400";
const SLATE = "bg-slate-100 text-slate-600 dark:bg-zink-600 dark:text-zink-200";

// A few actions have their own label; the rest are matched on their first word
// (APPROVE_INDXX, UPDATE_INDEX, SYNC_INDEX_VALUE, ...).
const FULL_ACTION_STYLES: { [action: string]: { label: string; className: string } } = {
  LOGIN: { label: "Signed in", className: GREEN },
  LOGOUT: { label: "Signed out", className: SLATE },
  SESSION_REPLACED: { label: "Session replaced", className: YELLOW },
  LOGIN_DENIED: { label: "Sign-in refused", className: RED },
  SYNC_BLOCKED: { label: "Sync blocked", className: RED },
};

const VERB_STYLES: { [verb: string]: { label: string; className: string } } = {
  APPROVE: { label: "Approved", className: SKY },
  PUBLISH: { label: "Published", className: GREEN },
  CREATE: { label: "Created", className: VIOLET },
  UPDATE: { label: "Updated", className: YELLOW },
  DELETE: { label: "Deleted", className: RED },
  UPLOAD: { label: "Uploaded", className: VIOLET },
  SYNC: { label: "Synced", className: SKY },
  PROCESS: { label: "Processed", className: VIOLET },
  CHANGE: { label: "Changed", className: YELLOW },
};

// Actions that are failures by definition, so they do not need a "Failed" tag as well.
const FAILURE_ACTIONS = ["LOGIN_DENIED", "SYNC_BLOCKED"];

const ENTITY_LABELS: { [type: string]: string } = {
  indxx: "Index",
  indexvalue: "Index value",
};

const titleCase = (value: string) =>
  value
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getActionDisplay = (action?: string) => {
  const code = (action || "").toUpperCase();
  const style = FULL_ACTION_STYLES[code] || VERB_STYLES[code.split("_")[0]];
  if (style) return style;
  return { label: titleCase(action || "-"), className: SLATE };
};

const getEntityLabel = (type?: string) => {
  if (!type) return "-";
  return ENTITY_LABELS[type.toLowerCase()] || titleCase(type);
};

// Old entries only store a sentence such as
// "Ajay Kumar Singh published the Indxx page with URL some-slug 10/8/2026, 1:54:34 PM",
// so the user and the page are read back out of it when the newer fields are missing.
const parseUserName = (text?: string) => {
  const match = (text || "").match(
    /^(.+?)\s+(?:approved|published|created|updated|deleted|logged|synced)\b/i
  );
  return match ? match[1].trim() : "";
};

// The sentence is "... with URL <slug>on <date>" for publishes (the "on" is glued to
// the slug) and "... with URL<slug> on <date>" for approvals, so both are handled.
const parseEntityName = (text?: string) => {
  const value = text || "";
  const withDate = value.match(
    /URL\s*(\S+?)(?:on)?\s+(?:on\s+)?\d{1,2}\/\d{1,2}\/\d{4}/i
  );
  if (withDate) return withDate[1];
  const loose = value.match(/URL\s*([^\s]+)/i);
  return loose ? loose[1].replace(/[.,;]+$/, "") : "";
};

// Old role, module and permission entries read "... with Name <name> on <date>".
const parseNamedItem = (text?: string) => {
  const match = (text || "").match(/with Name (.+?)\s+on\s+\d{1,2}\/\d{1,2}\/\d{4}/i);
  return match ? match[1].trim() : "";
};

const shortId = (id?: string) =>
  id && /^[a-f0-9]{24}$/i.test(id) ? `…${id.slice(-6)}` : id || "";

const getUser = (row: any) => ({
  name: row?.performed_by_name || parseUserName(row?.text) || row?.performed_by_email || "",
  detail: [row?.performed_by_email, row?.role_name].filter(Boolean).join(" · "),
});

// The item's name when there is one. Metadata publishes only ever recorded the item's id,
// so they say so instead of showing a short id that looks like a name.
const getEntityDisplay = (row: any) => {
  const name = row?.entity_name || parseEntityName(row?.text) || parseNamedItem(row?.text);
  if (name) return name;
  const id = shortId(row?.entity_id);
  return id && /_METADATA$/i.test(row?.action || "") ? `Metadata ${id}` : id;
};

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

type SortOrder = "desc" | "asc";

interface ListQuery {
  search: string;
  user: string;
  dateFrom: string;
  dateTo: string;
  pagination: PaginationState;
  order: SortOrder;
}

const Auditlog = () => {
  const [Auditlog, setAuditlog] = useState([]);
  const [search, setSearch] = useState<string>("");
  const [userFilter, setUserFilter] = useState<string>(""); // an admin's id, "" for everyone
  const [dateFrom, setDateFrom] = useState<string>(""); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState<string>("");
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  // Only the answer to the newest request is shown, so a slow older one cannot overwrite it.
  const latestRequest = useRef(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 30,
  });

  const debouncedHandler = useCallback(
    _.debounce((query: ListQuery) => {
      return fetchLog(query);
    }, 500),
    []
  );

  async function fetchLog({ search, user, dateFrom, dateTo, pagination, order }: ListQuery) {
    // The days are sent as whole moments in the browser's timezone: from the start of the
    // first day to the end of the last one.
    const filters: { [name: string]: string } = {};
    if (search.trim()) filters.qtext = search.trim();
    if (user) filters.user = user;
    if (dateFrom) filters.from = moment(dateFrom).startOf("day").toISOString();
    if (dateTo) filters.to = moment(dateTo).endOf("day").toISOString();

    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const localData = await callApi("v1/auditlog/list", "post", {
        pageNum: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sort: { order },
        filters,
      });
      if (requestId !== latestRequest.current) return;
      if (localData?.status === "Success") {
        setLoadError("");
        setAuditlog([...localData.data.auditlogList]);
        setPageCount(
          Math.ceil(localData.data.auditlogCount / pagination.pageSize)
        );
        setTotalItems(localData.data.auditlogCount);
      } else {
        setLoadError("Could not load the activity log. Please try again.");
      }
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setLoadError("Could not load the activity log. Please try again.");
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }

  // Changing any filter starts again from the first page.
  const firstPage = () => setPagination((prev) => ({ ...prev, pageIndex: 0 }));

  const changeSearch = (value: string) => {
    setSearch(value);
    firstPage();
  };

  const changeUser = (value: string) => {
    setUserFilter(value);
    firstPage();
  };

  const changeDateFrom = (value: string) => {
    setDateFrom(value);
    // keep the two days in order
    if (value && dateTo && value > dateTo) setDateTo(value);
    firstPage();
  };

  const changeDateTo = (value: string) => {
    setDateTo(value);
    if (value && dateFrom && value < dateFrom) setDateFrom(value);
    firstPage();
  };

  const clearFilters = () => {
    setSearch("");
    setUserFilter("");
    setDateFrom("");
    setDateTo("");
    firstPage();
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    firstPage();
  };

  const hasFilters = Boolean(search || userFilter || dateFrom || dateTo);

  useEffect(() => {
    debouncedHandler({
      search,
      user: userFilter,
      dateFrom,
      dateTo,
      pagination,
      order: sortOrder,
    });
  }, [search, userFilter, dateFrom, dateTo, pagination, sortOrder]);

  // The people the user filter offers: everyone who appears in the log.
  useEffect(() => {
    (async () => {
      try {
        const res = await callApi("v1/auditlog/users", "post", {});
        if (res?.status === "Success") {
          setUserOptions(
            (res.data.users || []).map((u: any) => ({
              value: u.id,
              label: u.name || u.email || `…${String(u.id).slice(-6)}`,
            }))
          );
        }
      } catch (error) {
        // The page still works without the list, only the user filter stays empty.
      }
    })();
  }, []);

  const AuditlogColumns = useMemo(
    () => [
      {
        header: () => (
          <button
            type="button"
            onClick={toggleSortOrder}
            className="inline-flex items-center gap-1 font-semibold"
            title={
              sortOrder === "desc"
                ? "Newest first. Click for oldest first."
                : "Oldest first. Click for newest first."
            }
          >
            Time
            {sortOrder === "desc" ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronUp className="size-4" />
            )}
          </button>
        ),
        id: "time",
        accessorKey: "performed_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const when = moment(row?.performed_at || row?.created_at);
          return (
            <div
              className="leading-tight"
              title={when.format("DD MMM YYYY, hh:mm:ss A")}
            >
              <div className="font-medium text-slate-700 dark:text-zink-100">
                {when.format("DD MMM YYYY")}
              </div>
              <div className="text-xs text-slate-500 dark:text-zink-200">
                {when.format("hh:mm A")}
              </div>
            </div>
          );
        },
      },
      {
        header: "User",
        accessorKey: "performed_by",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const user = getUser(cell.row.original);
          if (!user.name) {
            return <span className="text-slate-400">-</span>;
          }
          return (
            <div className="flex items-center gap-2.5" title={user.detail || undefined}>
              <span className="flex items-center justify-center text-xs font-semibold rounded-full size-8 shrink-0 bg-custom-100 text-custom-500 dark:bg-custom-500/20">
                {getInitials(user.name)}
              </span>
              <span className="font-medium text-slate-700 dark:text-zink-100">
                {user.name}
              </span>
            </div>
          );
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const display = getActionDisplay(row?.action);
          // There is no Status column: nearly everything succeeds, so only a failure is
          // marked. "Sign-in refused" and "Sync blocked" already say so themselves.
          const showFailed = row?.success === false && !FAILURE_ACTIONS.includes(row?.action);
          return (
            <div className="flex items-center gap-1.5">
              <span
                className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded border border-transparent ${display.className}`}
                title={row?.action}
              >
                {display.label}
              </span>
              {showFailed ? (
                <span
                  className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded ${RED}`}
                  title={row?.status_code ? `HTTP ${row.status_code}` : undefined}
                >
                  Failed
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        header: "Item",
        accessorKey: "entity_type",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const name = getEntityDisplay(row);
          const tooltip = [
            row?.text,
            row?.entity_id ? `ID ${row.entity_id}` : "",
            row?.method && row?.path ? `${row.method} ${row.path}` : "",
            row?.ip ? `IP ${row.ip}` : "",
          ]
            .filter(Boolean)
            .join("\n");
          return (
            <div className="flex items-start gap-2" title={tooltip || undefined}>
              <span className="inline-block px-2 py-0.5 mt-px text-xs font-medium rounded shrink-0 bg-slate-100 text-slate-600 dark:bg-zink-600 dark:text-zink-200">
                {getEntityLabel(row?.entity_type)}
              </span>
              {name ? (
                // Long names wrap onto more lines, so the whole name is always readable.
                <span className="min-w-0 max-w-2xl break-words whitespace-normal text-slate-600 dark:text-zink-100">
                  {name}
                </span>
              ) : null}
            </div>
          );
        },
      },
    ],
    [sortOrder]
  );

  return (
    <>
      <React.Fragment>
        <BreadCrumb
          title="Activity Logs"
          pageTitle="Activity Logs"
          version=""
        />
        <ToastContainer closeButton={false} limit={1} />
        <div className="grid grid-cols-12 gap-x-5">
          <div className="col-span-12 card 2xl:col-span-12">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Activity Logs</h6>
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-12 xl:items-end">
                  <div className="xl:col-span-3">
                    <label className="block mb-1 font-medium">Search</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={search}
                        className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="User, action or item..."
                        autoComplete="off"
                        onChange={(e) => changeSearch(e.target.value)}
                      />
                      <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                    </div>
                  </div>
                  <div className="xl:col-span-3">
                    <CustomSelect
                      label="User"
                      name="user"
                      value={userFilter}
                      onChange={(value) => changeUser(value as string)}
                      options={[{ label: "All users", value: "" }, ...userOptions]}
                      isSearchable
                    />
                  </div>
                  <div className="xl:col-span-2">
                    <label className="block mb-1 font-medium">From</label>
                    <input
                      type="date"
                      className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:text-zink-100 dark:bg-zink-700"
                      value={dateFrom}
                      max={dateTo || moment().format("YYYY-MM-DD")}
                      onChange={(e) => changeDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="xl:col-span-2">
                    <label className="block mb-1 font-medium">To</label>
                    <input
                      type="date"
                      className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:text-zink-100 dark:bg-zink-700"
                      value={dateTo}
                      min={dateFrom || undefined}
                      max={moment().format("YYYY-MM-DD")}
                      onChange={(e) => changeDateTo(e.target.value)}
                    />
                  </div>
                  <div className="xl:col-span-2">
                    {hasFilters ? (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="w-full text-slate-500 btn bg-slate-100 border-slate-100 hover:text-slate-600 hover:bg-slate-200 hover:border-slate-200 dark:bg-zink-600 dark:border-zink-600 dark:text-zink-200 dark:hover:bg-zink-500"
                      >
                        Clear filters
                      </button>
                    ) : null}
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              {loadError ? (
                <div className="px-4 py-3 mb-4 text-sm text-red-600 rounded-md bg-red-50 dark:bg-red-500/20 dark:text-red-400">
                  {loadError}
                </div>
              ) : null}
              {!loading && !loadError && Auditlog.length === 0 ? (
                <div className="py-10 text-center text-slate-500 dark:text-zink-200">
                  {hasFilters ? "No activity matches your filters." : "No activity has been recorded yet."}
                </div>
              ) : (
                <div className={loading ? "opacity-60 transition-opacity" : ""}>
                  <TableContainer
                    isPagination={true}
                    columns={AuditlogColumns || []}
                    data={Auditlog || []}
                    pageCount={pageCount}
                    pagination={pagination}
                    totalItems={totalItems}
                    onPaginationChange={(updater: any) => {
                      setPagination(
                        typeof updater === "function"
                          ? updater(pagination)
                          : updater
                      );
                    }}
                    divclassName="overflow-x-auto"
                    tableclassName="w-full whitespace-nowrap"
                    theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                    thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500"
                    tbodyclassName="[&>tr:hover]:bg-slate-50 dark:[&>tr:hover]:bg-zink-600/50"
                    tdclassName="px-3.5 py-3 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500 align-middle"
                    PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    </>
  );
};

export default Auditlog;
